/**
 * <cart-free-gift>
 *
 * Web component qui tient le panier dans un état cohérent vis-à-vis du palier :
 *   - au-dessus du palier : EXACTEMENT un cadeau, en quantité 1 ;
 *   - en dessous           : aucun cadeau.
 * À chaque évaluation, il NORMALISE le panier vers cet état (surplus retirés,
 * quantité ramenée à 1) au lieu de seulement réagir à « présent / absent ».
 *
 * Ce module est un confort d'interface, pas une protection : tout ce qu'il fait
 * peut être contourné par une requête directe à l'API panier. La gratuité doit
 * être garantie côté Shopify (réduction automatique conditionnée au palier).
 *
 * Verrou : lock() / release() avec compteur, partagé avec <cart-gift-selector>
 * pendant un échange ou un refus. Un changement de panier survenu pendant le
 * verrou n'est PAS perdu : il est mémorisé et réévalué dès la libération, sur
 * un panier relu au serveur.
 *
 * Refus : localStorage.gift_refused. Effacé dès que le panier repasse sous le
 * palier, pour que le refus ne vaille que pour le franchissement en cours.
 */

const STORAGE_REFUSED_KEY = 'gift_refused';

/** Laisse retomber les cart:update déclenchés par nos propres requêtes. */
const LOCK_SETTLE_MS = 600;

class CartFreeGift extends HTMLElement {
  #locks = 0;
  #pending = false;
  #abortController = null;
  /** Variantes que Shopify a refusé d'ajouter (rupture) pendant ce passage au-dessus du palier. */
  #unavailable = new Set();

  connectedCallback() {
    this.GIFT_IDS = (this.dataset.giftIds || '')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter(Boolean);
    this.DEFAULT_GIFT_ID = parseInt(this.dataset.defaultGiftId, 10) || this.GIFT_IDS[0];
    // 0 € est un réglage valide (cadeau dès le premier achat) : surtout pas de `|| défaut`,
    // qui transformait un seuil à 0 en 60 €. Seuil illisible = module désactivé.
    this.GIFT_THRESHOLD = parseInt(this.dataset.threshold, 10);
    this.SECTION_ID = this.dataset.sectionId || '';

    if (!this.GIFT_IDS.length || !this.DEFAULT_GIFT_ID || !Number.isFinite(this.GIFT_THRESHOLD)) {
      return;
    }

    this.#abortController = new AbortController();
    document.addEventListener('cart:update', this.#handleCartUpdate, { signal: this.#abortController.signal });

    this.#run();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  // ===== Verrou partagé =====

  get isUpdating() {
    return this.#locks > 0;
  }

  lock() {
    this.#locks += 1;
  }

  /**
   * @param {{ settle?: boolean }} [options] settle : attendre que nos propres
   *   cart:update retombent avant de libérer (après une écriture dans le panier).
   */
  release({ settle = true } = {}) {
    const done = () => {
      this.#locks = Math.max(0, this.#locks - 1);
      if (this.#locks === 0 && this.#pending) {
        this.#pending = false;
        queueMicrotask(() => this.#run());
      }
    };
    if (settle) setTimeout(done, LOCK_SETTLE_MS);
    else done();
  }

  // ===== Lecture du panier =====

  /**
   * Lit les IDs de cadeaux depuis le panel s'il existe, pour rester cohérent
   * avec la config en cas de re-render.
   */
  getAllGiftIds() {
    const panel = document.querySelector('cart-gift-selector');
    if (panel) {
      const buttons = panel.querySelectorAll('[data-action="select-gift"][data-variant-id]');
      const ids = Array.from(buttons)
        .map((btn) => parseInt(btn.dataset.variantId, 10))
        .filter(Boolean);
      if (ids.length) return ids;
    }
    return this.GIFT_IDS;
  }

  #giftLines(cart) {
    const ids = this.getAllGiftIds();
    return (cart.items || []).filter((item) => ids.includes(item.variant_id));
  }

  // ===== Évaluation =====

  #handleCartUpdate = (event) => {
    // Nos propres dispatchs : le panier est déjà dans l'état voulu.
    if (event?.detail?.sourceId === 'cart-free-gift') return;

    const cart = event?.detail?.resource;
    // resource peut être absent OU partiel : product-form dispatche CartAddEvent avec
    // resource = {} (pas de total_price/items) -> on relit le vrai panier.
    const complete = cart && cart.items && cart.total_price !== undefined;
    this.#run(complete ? cart : null);
  };

  /** @param {object|null} cartArg panier déjà connu, ou null pour le relire au serveur */
  async #run(cartArg = null) {
    if (this.isUpdating) {
      this.#pending = true;
      return;
    }
    this.lock();
    let wrote = false;
    try {
      const cart = cartArg || (await this.#fetchCart());
      if (cart) wrote = await this.#reconcile(cart);
    } finally {
      this.release({ settle: wrote });
    }
  }

  /**
   * Amène le panier dans l'état attendu. Renvoie true si une écriture a eu lieu.
   * @returns {Promise<boolean>}
   */
  async #reconcile(cart) {
    const giftLines = this.#giftLines(cart);
    const paidLines = (cart.items || []).filter((item) => !giftLines.includes(item));

    // Le palier se mesure sur les articles payants seuls : le jour où les cadeaux
    // retrouvent un prix catalogue, leur valeur ne doit pas aider à franchir le palier.
    const giftValue = giftLines.reduce((sum, line) => sum + (line.final_line_price || 0), 0);
    const total = cart.total_price - giftValue;
    // Au moins un article payant : sans ça, un palier à 0 € offrirait un cadeau à un panier vide.
    const above = paidLines.length > 0 && total >= this.GIFT_THRESHOLD;

    if (!above) {
      localStorage.removeItem(STORAGE_REFUSED_KEY);
      this.#unavailable.clear();
      if (!giftLines.length) return false;
      return Boolean(await this.#update(Object.fromEntries(giftLines.map((line) => [line.key, 0]))));
    }

    if (!giftLines.length) {
      if (localStorage.getItem(STORAGE_REFUSED_KEY) === 'true') return false;
      return this.#addFirstAvailable();
    }

    // Au-dessus du palier avec au moins un cadeau : on en garde un seul, en
    // quantité 1. Shopify liste le panier du plus récent au plus ancien, donc
    // c'est le dernier ajouté qui reste. Plusieurs cadeaux à la fois ne naissent
    // que d'ajouts forcés : le parcours normal (échange) n'en laisse qu'un.
    const [keep, ...extra] = giftLines;
    const updates = {};
    if (keep.quantity !== 1) updates[keep.key] = 1;
    for (const line of extra) updates[line.key] = 0;
    if (!Object.keys(updates).length) return false;
    return Boolean(await this.#update(updates));
  }

  /**
   * Ajoute le cadeau par défaut, ou à défaut le suivant encore en stock. Si
   * aucun ne passe, on n'insiste plus jusqu'au prochain passage sous le palier :
   * le tiroir affiche alors sa ligne « sans cadeau » (cart-products.liquid).
   */
  async #addFirstAvailable() {
    const ids = this.getAllGiftIds();
    const order = [this.DEFAULT_GIFT_ID, ...ids.filter((id) => id !== this.DEFAULT_GIFT_ID)];
    for (const id of order) {
      if (this.#unavailable.has(id)) continue;
      // cart/update fixe la quantité à 1 (idempotent) : rejouée deux fois, la
      // requête donne toujours un seul cadeau — cart/add, lui, incrémentait.
      const cart = await this.#update({ [id]: 1 });
      // Une réponse acceptée ne prouve pas l'ajout : on exige la ligne dans le
      // panier renvoyé, sinon une rupture silencieuse ferait réessayer en boucle.
      if (cart?.items?.some((item) => item.variant_id === id)) return true;
      this.#unavailable.add(id);
    }
    return false;
  }

  // ===== Réseau =====

  async #fetchCart() {
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  /** @returns {Promise<object|null>} le panier renvoyé par Shopify, ou null en cas de refus */
  async #update(updates) {
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: this.SECTION_ID }),
      });
      if (!res.ok) return null;
      const cart = await res.json();
      this.#refreshDrawer(cart);
      return cart;
    } catch (e) {
      return null;
    }
  }

  #refreshDrawer(cartData) {
    // Re-render via Section Rendering API si disponible
    const sectionHtml = cartData?.sections?.[this.SECTION_ID];
    if (sectionHtml) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const newCartItems = doc.querySelector('cart-items-component');
        const oldCartItems = document.querySelector(`cart-items-component[data-section-id="${this.SECTION_ID}"]`);
        if (newCartItems && oldCartItems) {
          oldCartItems.innerHTML = newCartItems.innerHTML;
        }
      } catch (e) {}
    }
    // Dispatch un event que les autres composants peuvent écouter
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cartData, sourceId: 'cart-free-gift' },
      })
    );
  }
}

if (!customElements.get('cart-free-gift')) {
  customElements.define('cart-free-gift', CartFreeGift);
}
